import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";

export interface BreadcrumbItem {
  label: string;
  /** Omit ``to`` for the final, non-link crumb. */
  to?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Minimal breadcrumb trail for detail pages. The final item
 * renders as plain text (the current page). All earlier crumbs
 * are React Router links.
 */
export function Breadcrumb({ items, className }: BreadcrumbProps) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <nav
      aria-label={t("shared.breadcrumb.ariaLabel")}
      className={cn("text-xs", className)}
    >
      <ol className="flex flex-wrap items-center gap-1.5 text-neutral-500">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <li key={`${item.label}-${idx}`} className="flex items-center gap-1.5">
              {item.to && !isLast ? (
                <Link
                  to={item.to}
                  className="font-medium text-ocean-600 transition-colors hover:text-ocean-800"
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  className={isLast ? "font-medium text-ocean-900" : ""}
                  aria-current={isLast ? "page" : undefined}
                >
                  {item.label}
                </span>
              )}
              {!isLast && (
                <ChevronRight
                  size={12}
                  className="shrink-0 text-ocean-300"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
